import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function ImportLabelModal({
  show,
  onClose,
  refreshLabels,
  formData,
  setFormData,
  selectedEmail,
  labels,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */

  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationFetchError, setLocationFetchError] = useState("");
  const [labelsLoading, setLabelsLoading] = useState(false);

  /*   All Functions
   ********************************************* */

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData?.labelId) {
      setFormError("Please select a label.");
      return;
    }

    if (!formData?.locationId) {
      setFormError("Please select a location.");
      return;
    }

    setFormError("");
    setSubmitting(true);

    try {
      await API.post(
        "/email-location/import",
        {
          labelId: formData.labelId,
          labelName: formData.labelName,
          locationId: formData.locationId,
          grantEmail: selectedEmail,
        },
        {
          headers: authHeader(),
        }
      );

      dispatch(
        setNotification({
          type: "success",
          message: "Label imported successfully.",
        })
      );

      if (setFormData) {
        setFormData({ labelId: "", labelName: "", locationId: "" });
      }

      if (refreshLabels) {
        refreshLabels();
      }

      handleClose();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to import label.";
      setFormError(message);
      dispatch(setNotification({ type: "error", message }));
    } finally {
      setSubmitting(false);
    }
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    if (!show) {
      setFormError("");
      setSubmitting(false);
      setLocations([]);
      setLocationFetchError("");

      if (setFormData) {
        setFormData({ labelId: "", labelName: "", locationId: "" });
      }
    }
  }, [show, setFormData]);

  useEffect(() => {
    if (!show) return;

    let isActive = true;

    const fetchData = async () => {
      setLocationsLoading(true);
      setLabelsLoading(true);
      setLocationFetchError("");

      try {
        // Fetch locations and labels in parallel
        const { data } = await API.get("/emails/locations/list", {
          headers: authHeader(),
        });

        if (!isActive) return;

        if (Array.isArray(data?.locations)) {
          setLocations(data.locations);
        }
      } catch (error) {
        if (!isActive) return;
        const message = error?.message || "Failed to load data.";
        setLocationFetchError(message);
        dispatch(
          setNotification({
            type: "error",
            message,
          })
        );
      } finally {
        if (isActive) {
          setLocationsLoading(false);
          setLabelsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [dispatch, show]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      contentClassName="custom-shadow-sm px-2 rounded-3"
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Import Label</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="labelSelect">
            <Form.Label>Select Label</Form.Label>
            <Form.Select
              value={formData?.labelId || ""}
              onChange={(event) => {
                const labelId = event.target.value;
                const selectedLabel = labels?.find(
                  (label) => String(label.id) === String(labelId)
                );
                setFormData({
                  ...formData,
                  labelId,
                  labelName: selectedLabel?.name || "",
                });
                if (formError) setFormError("");
              }}
              disabled={submitting || labelsLoading}
            >
              <option value="">Select a label</option>
              {labels?.map((label) => (
                <option key={label.id} value={label.id}>
                  {label.name}
                </option>
              ))}
            </Form.Select>
            {labelsLoading && (
              <div className="small text-muted mt-2">Loading labels...</div>
            )}
          </Form.Group>

          <Form.Group controlId="labelLocation" className="mt-3">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={formData?.locationId || ""}
              onChange={(event) => {
                setFormData({
                  ...formData,
                  locationId: event.target.value,
                });
                if (formError) setFormError("");
              }}
              disabled={submitting || locationsLoading}
            >
              <option value="">Select a location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.locationName}
                </option>
              ))}
            </Form.Select>
            {locationsLoading && (
              <div className="small text-muted mt-2">Loading locations...</div>
            )}
            {locationFetchError && (
              <div className="text-danger small mt-2">{locationFetchError}</div>
            )}
          </Form.Group>

          {formError && (
            <div className="alert alert-danger small mt-3">
              {formError === "Conflict" ? "Already Exists" : formError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting || locationsLoading || labelsLoading}
          >
            {submitting ? "Importing..." : "Import Label"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
